<template>
    <div class="np-section">
        <!-- Notice Period Card -->
        <div class="np-card">
            <div class="np-card__header">
                <div class="np-card__title"><i class="fas fa-hourglass-half"></i> Notice Period</div>
            </div>

            <div class="np-card__body" v-if="loading">
                <div class="np-loading"><i class="fas fa-spinner fa-spin"></i> Loading...</div>
            </div>

            <div class="np-card__body" v-else-if="!employee.designation">
                <div class="np-alert np-alert--warn">
                    <i class="fas fa-exclamation-triangle"></i>
                    Designation not assigned to employee!
                </div>
            </div>

            <div class="np-card__body" v-if="employee && employee.designation">
                <!-- Active Notice Period -->
                <div v-if="employee.noticePeriodStartDate && employee.noticePeriodEndDate">
                    <div class="np-active-banner">
                        <div class="np-active-banner__icon"><i class="fas fa-bell"></i></div>
                        <div class="np-active-banner__content">
                            <span class="np-active-banner__label">Notice Period Active</span>
                            <div class="np-active-banner__dates">
                                <span><strong>Start:</strong> {{ employee.noticePeriodStartDate }}</span>
                                <span class="np-date-sep">→</span>
                                <span><strong>End:</strong> {{ employee.noticePeriodEndDate }}</span>
                            </div>
                        </div>
                    </div>

                    <div class="np-detail-row" v-if="employee.noticePeriodResignLetterUrl">
                        <span class="np-detail-label"><i class="fas fa-paperclip"></i> Resign Letter</span>
                        <OldImageView linkMode :src="employee.noticePeriodResignLetterUrl" />
                    </div>

                    <div class="np-detail-row" v-if="employee.noticePeriodNotes">
                        <span class="np-detail-label"><i class="fas fa-sticky-note"></i> Notes</span>
                        <p class="np-detail-text">{{ employee.noticePeriodNotes }}</p>
                    </div>

                    <div class="np-card__footer">
                        <button class="np-btn np-btn--danger" @click="cancel" :disabled="processing">
                            <i class="fas fa-times-circle" v-if="!processing"></i>
                            <i class="fas fa-spinner fa-spin" v-else></i>
                            {{ processing ? "Cancelling..." : "Cancel Notice Period" }}
                        </button>
                    </div>
                </div>

                <!-- Create Notice Period Form -->
                <div v-else>
                    <div class="np-form-grid">
                        <div class="np-field">
                            <custom-input type="number" :min="0" v-model="noticePeriodDays" required label="Notice Period Days" />
                        </div>
                        <div class="np-field">
                            <custom-input type="date" v-model="startDate" label="Start Date" required :min="minDate" />
                        </div>
                        <div class="np-field">
                            <custom-input type="date" disabled v-model="endDate" label="End Date (Auto)" required :min="minDate" />
                        </div>
                        <div class="np-field">
                            <custom-input type="textarea" v-model="noticePeriodNotes" label="Notes" />
                        </div>
                        <div class="np-field">
                            <custom-input type="file" v-model="noticePeriodResignLetterUrl" label="Upload Resign Letter" />
                        </div>
                    </div>
                    <div class="np-card__footer">
                        <button class="np-btn np-btn--primary" @click="submit" :disabled="processing">
                            <i class="fas fa-paper-plane" v-if="!processing"></i>
                            <i class="fas fa-spinner fa-spin" v-else></i>
                            {{ processing ? "Submitting..." : "Submit Notice Period" }}
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Termination Card -->
        <div class="np-card" v-if="employee && employee.designation">
            <div class="np-card__header">
                <div class="np-card__title np-card__title--danger"><i class="fas fa-user-slash"></i> Employee Termination</div>
            </div>
            <div class="np-card__body">
                <div class="np-form-grid np-form-grid--2col">
                    <div class="np-field">
                        <custom-input v-model="terminationOfferedByPersonName" type="text" required label="Termination Offered By (Name)" />
                    </div>
                    <div class="np-field">
                        <custom-input v-model="terminationOfferedByPersonPosition" type="text" required label="Person's Position" />
                    </div>
                </div>

                <div class="np-term-info" v-if="employee.terminationLetterLastGeneratedDate">
                    <i class="fas fa-info-circle"></i>
                    Last generated on <strong>{{ formatIndianDate(employee.terminationLetterLastGeneratedDate) }}</strong>
                </div>

                <div class="np-card__footer np-card__footer--actions">
                    <button class="np-btn np-btn--dark" @click="generateTerminationLetter" :disabled="terminating">
                        <i class="fas fa-spinner fa-spin" v-if="terminating"></i>
                        <i class="fas fa-file-alt" v-else-if="!employee.terminationLetterPdf"></i>
                        <i class="fas fa-redo" v-else></i>
                        {{ terminating ? "Generating..." : employee.terminationLetterPdf ? "Regenerate Letter" : "Generate Termination Letter" }}
                    </button>
                    <button
                        v-if="employee.terminationLetterPdf"
                        class="np-btn np-btn--outline"
                        @click="downloadTerminationLetter(employee.terminationLetterPdf)"
                    >
                        <i class="fas fa-download"></i> Download Letter
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { daysToMs, formatIndianDate, getAssetUrlPath } from "../../utils/common"
import { http } from "../../utils/http"
import CustomInput from "../crud/custom-input.vue"
import Button from "../UI/Button.vue"
import OldImageView from "../UI/OldImageView.vue"
import SectionTitle from "../UI/SectionTitle.vue"

export default {
    components: { CustomInput, SectionTitle, Button, OldImageView },
    data() {
        return {
            noticePeriodResignLetterUrl: null,
            noticePeriodDays: 0,
            loading: false,
            employee: "",
            startDate: "",
            endDate: "",
            processing: false,
            noticePeriodNotes: "",
            minDate: new Date().toISOString().slice(0, 10),
            terminating: false,
            terminationOfferedByPersonName: "",
            terminationOfferedByPersonPosition: "",
        }
    },
    props: {
        empId: { type: [String, Number] },
    },
    async mounted() {
        // noticePeriodStartDate
        // noticePeriodEndDate
        this.fetchUser()
    },
    methods: {
        formatIndianDate,
        downloadTerminationLetter(path) {
            window.open(getAssetUrlPath(path), "_blank")
        },
        async generateTerminationLetter() {
            if (!this.terminationOfferedByPersonName || !this.terminationOfferedByPersonPosition) {
                this.$toast.error("Please fillup the fields required for termination letter!")
                return
            }
            this.terminating = true
            try {
                const res = await http.post(
                    `/employees/${this.employee.id}/generate-termination-letter/`,
                    {
                        terminationOfferedByPersonName: this.terminationOfferedByPersonName,
                        terminationOfferedByPersonPosition: this.terminationOfferedByPersonPosition,
                    }
                )
                this.$toast.success("Termination letter generated!")
                window.location.href = `${this.$route.path}?active_tab=NOTICE_PERIOD`
            } catch (err) {
                console.log("error in generating termination letter", err)
                this.$toast.error("Error in generating termination letter!")
            }
            this.terminating = false
        },
        async cancel() {
            if (this.processing) return
            try {
                if (!this.noticePeriodDays || Number(this.noticePeriodDays) < 0) {
                    this.$toast.error("Please enter a positive integer for notice period days!")
                    return
                }
                this.processing = true
                await http.post("/notice-periods/cancel", {
                    employeeId: this.employee.id,
                })
                window.location.href = this.$route.path + "?active_tab=NOTICE_PERIOD"
            } catch (err) {
                console.log("error in cancelling notice period", err)
            }
            this.processing = false
        },
        async submit() {
            if (!this.startDate) {
                this.$toast.error("Please enter start date!")
                return
            }
            if (this.processing) return
            try {
                this.processing = true

                const formData = new FormData()
                formData.append("startDate", this.startDate)
                formData.append("endDate", this.endDate)
                formData.append("employeeId", this.employee.id)
                formData.append("noticePeriodNotes", this.noticePeriodNotes)
                formData.append("noticePeriodResignLetterUrl", this.noticePeriodResignLetterUrl)
                await http.post("/notice-periods/create", formData)
                window.location.href = this.$route.path + "?active_tab=NOTICE_PERIOD"
            } catch (err) {
                console.log("error in creating notice period", err)
            }
            this.processing = false
        },
        async fetchUser() {
            if (this.loading) return
            this.loading = true
            try {
                const res = await http.get(`/users/${this.empId}`)
                this.employee = res.data.result
                this.terminationOfferedByPersonName =
                    this.employee.terminationOfferedByPersonName || ""
                this.terminationOfferedByPersonPosition =
                    this.employee.terminationOfferedByPersonPosition || ""
            } catch (err) {
                console.log("error in fetching user", err)
            }
            this.loading = false
        },
        updateDates() {
            if (this.startDate) {
                const addDays = daysToMs(this.noticePeriodDays || 30)
                this.endDate = new Date(new Date(this.startDate).getTime() + addDays)
                    .toISOString()
                    .slice(0, 10)
            }
        },
    },
    watch: {
        employee(e) {
            if (e) {
                this.noticePeriodDays = this.employee.designation.noticePeriodDays
            }
        },
        startDate(e) {
            this.updateDates()
        },
        noticePeriodDays(e) {
            this.updateDates()
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../assets/variables.scss";

.np-section {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

/* ── Card ── */
.np-card {
    background: white;
    border: 1px solid $gray100;
    border-radius: $radiusMd;
    box-shadow: $shadowSm;
    overflow: hidden;
}
.np-card__header {
    padding: 14px 20px;
    border-bottom: 1px solid $gray100;
    background: $gray50;
}
.np-card__title {
    font-size: 15px;
    font-weight: 700;
    color: $gray800;
    display: flex;
    align-items: center;
    gap: 8px;

    i { color: $primaryColor; font-size: 14px; }

    &--danger i { color: $danger; }
}
.np-card__body {
    padding: 20px;
}
.np-card__footer {
    padding-top: 16px;
    margin-top: 12px;
    border-top: 1px solid $gray100;

    &--actions {
        display: flex;
        align-items: center;
        gap: 10px;
        flex-wrap: wrap;
    }
}

/* ── Loading / Alert ── */
.np-loading {
    text-align: center;
    padding: 24px;
    color: $gray500;
    font-size: 13px;

    i { margin-right: 6px; }
}
.np-alert {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 16px;
    border-radius: $radiusSm;
    font-size: 13px;
    font-weight: 500;

    &--warn {
        background: lighten($warning, 32%);
        color: darken($warning, 18%);
        border: 1px solid lighten($warning, 20%);
    }
}

/* ── Active Notice Banner ── */
.np-active-banner {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 16px 18px;
    background: linear-gradient(135deg, $primaryLight, lighten($primaryColor, 38%));
    border: 1px solid lighten($primaryColor, 30%);
    border-radius: $radiusSm;
    margin-bottom: 16px;
}
.np-active-banner__icon {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: white;
    color: $primaryColor;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    flex-shrink: 0;
    box-shadow: 0 2px 6px rgba(0,0,0,0.08);
}
.np-active-banner__content {
    display: flex;
    flex-direction: column;
    gap: 4px;
}
.np-active-banner__label {
    font-size: 11px;
    font-weight: 700;
    color: $primaryDark;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}
.np-active-banner__dates {
    font-size: 13px;
    color: $gray700;
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}
.np-date-sep {
    color: $primaryColor;
    font-weight: 600;
}

/* ── Detail Rows ── */
.np-detail-row {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-bottom: 12px;
}
.np-detail-label {
    font-size: 11px;
    font-weight: 600;
    color: $gray500;
    text-transform: uppercase;
    letter-spacing: 0.4px;

    i { margin-right: 4px; font-size: 10px; color: $gray400; }
}
.np-detail-text {
    font-size: 13px;
    color: $gray700;
    margin: 0;
    line-height: 1.5;
}

/* ── Form Grid ── */
.np-form-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;

    &--2col {
        grid-template-columns: repeat(2, 1fr);
    }
}
.np-field {
    min-width: 0;
}

/* ── Termination Info ── */
.np-term-info {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: $gray500;
    margin-top: 12px;

    i { color: $primaryColor; }
    strong { color: $gray700; }
}

/* ── Buttons ── */
.np-btn {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 9px 20px;
    border-radius: $radiusSm;
    font-size: 13px;
    font-weight: 600;
    font-family: inherit;
    cursor: pointer;
    border: none;
    transition: all $transitionFast;

    &:disabled {
        opacity: 0.55;
        cursor: not-allowed;
    }

    &--primary {
        background: $primaryColor;
        color: white;
        &:hover:not(:disabled) { background: $primaryDark; }
    }
    &--danger {
        background: $danger;
        color: white;
        &:hover:not(:disabled) { background: darken($danger, 6%); }
    }
    &--dark {
        background: $gray800;
        color: white;
        &:hover:not(:disabled) { background: $gray900; }
    }
    &--outline {
        background: white;
        color: $primaryColor;
        border: 1.5px solid $primaryColor;
        &:hover:not(:disabled) { background: $primaryLight; }
    }
}

/* ── Responsive ── */
@media (max-width: 768px) {
    .np-form-grid, .np-form-grid--2col {
        grid-template-columns: 1fr;
    }
    .np-active-banner { flex-direction: column; align-items: flex-start; }
    .np-active-banner__dates { flex-direction: column; gap: 2px; }
}
</style>
